/**
 * Form validation helper functions
 * Used throughout the application for consistent validation
 */

/**
 * Validate email address format
 * @param email - Email address to validate
 * @returns true if valid, error message if invalid
 */
export function validateEmail(email: string): true | string {
    if (!email || email.trim() === "") {
        return "Email is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        return "Please enter a valid email address";
    }

    return true;
}

/**
 * Validate that a field has a value
 * @param value - Value to validate
 * @param fieldName - Name of the field (for error message)
 * @returns true if valid, error message if invalid
 */
export function validateRequired(
    value: any,
    fieldName: string = "This field",
): true | string {
    if (value === null || value === undefined) {
        return `${fieldName} is required`;
    }

    if (typeof value === "string" && value.trim() === "") {
        return `${fieldName} is required`;
    }

    if (Array.isArray(value) && value.length === 0) {
        return `${fieldName} must have at least one item`;
    }

    return true;
}

/**
 * Validate organization name format
 * Must be alphanumeric with no spaces (can include hyphens and underscores)
 * Used for subdomain generation
 * @param orgName - Organization name to validate
 * @returns true if valid, error message if invalid
 */
export function validateOrgName(orgName: string): true | string {
    if (!orgName || orgName.trim() === "") {
        return "Organization name is required";
    }

    // Must be alphanumeric (lowercase), can include hyphens and underscores, no spaces
    const orgNameRegex = /^[a-z0-9_-]+$/;
    if (!orgNameRegex.test(orgName)) {
        return "Organization name must be lowercase alphanumeric (can include hyphens and underscores, no spaces)";
    }

    // Minimum length check
    if (orgName.length < 3) {
        return "Organization name must be at least 3 characters long";
    }

    // Maximum length check (subdomain restrictions)
    if (orgName.length > 63) {
        return "Organization name must be less than 63 characters";
    }

    // Cannot start or end with hyphen
    if (orgName.startsWith("-") || orgName.endsWith("-")) {
        return "Organization name cannot start or end with a hyphen";
    }

    return true;
}

/**
 * Format API error for display to users
 * Extracts meaningful error messages from various API response formats
 * @param error - Error object from API call
 * @returns User-friendly error message
 */
export function formatApiError(error: any): string {
    // Default error message
    const defaultMessage = "An unexpected error occurred. Please try again.";

    if (!error) {
        return defaultMessage;
    }

    // If error has a message property
    if (typeof error === "string") {
        return error;
    }

    // Axios error format
    if (error.response) {
        const { data, status } = error.response;

        // Handle specific HTTP status codes
        if (status === 400) {
            return data?.message || "Invalid request. Please check your input.";
        }
        if (status === 401) {
            return "You are not authorized. Please sign in again.";
        }
        if (status === 403) {
            return "You don't have permission to perform this action.";
        }
        if (status === 404) {
            return data?.message || "The requested resource was not found.";
        }
        if (status === 409) {
            return (
                data?.message ||
                "A conflict occurred. The resource may already exist."
            );
        }
        if (status >= 500) {
            return "Server error. Please try again later.";
        }

        // Try to extract message from various formats
        if (data?.message) {
            return data.message;
        }
        if (data?.error) {
            return typeof data.error === "string"
                ? data.error
                : data.error.message;
        }
        if (typeof data === "string") {
            return data;
        }
    }

    // Network error
    if (error.request && !error.response) {
        return "Network error. Please check your internet connection.";
    }

    // Error with message property
    if (error.message) {
        return error.message;
    }

    return defaultMessage;
}

/**
 * Validate string length
 * @param value - String to validate
 * @param min - Minimum length
 * @param max - Maximum length
 * @param fieldName - Field name for error messages
 * @returns true if valid, error message if invalid
 */
export function validateLength(
    value: string,
    min: number,
    max: number,
    fieldName: string = "This field",
): true | string {
    if (!value) {
        return `${fieldName} is required`;
    }

    const length = value.trim().length;

    if (length < min) {
        return `${fieldName} must be at least ${min} characters`;
    }

    if (length > max) {
        return `${fieldName} must be less than ${max} characters`;
    }

    return true;
}

/**
 * Validate URL format
 * @param url - URL to validate
 * @returns true if valid, error message if invalid
 */
export function validateUrl(url: string): true | string {
    if (!url || url.trim() === "") {
        return "URL is required";
    }

    try {
        new URL(url);
        return true;
    } catch {
        return "Please enter a valid URL";
    }
}

/** Loose phone check shared by sign-up and edit profile: digits and common separators, at least 7 digits. */
export function isValidPhoneNumber(value: string): boolean {
    return (
        /^\+?[\d\s().-]+$/.test(value) && value.replace(/\D/g, "").length >= 7
    );
}

/** NestJS validation errors return `message` as an array. */
export function getApiErrorMessage(error: any, fallback: string): string {
    const message = error?.response?.data?.message;
    if (Array.isArray(message)) return message.join(" ");
    return message || fallback;
}
